/**
 * useVirtualization - Helper hook for calendar virtualization
 * Provides utilities for calculating item sizes, scroll positions, and day indices
 */

import { useMemo, useCallback, RefObject } from 'react';
import { isSameDay, isSameMonth, getDate } from 'date-fns';
import { VariableSizeList } from 'react-window';
import { ROW_HEIGHT_PX, GAP_HEIGHT_PX, MONTH_DIVIDER_HEIGHT_PX } from '@/components/shifts/constants';

interface DayRowCalculation {
  dayDate: Date;
  dayOfMonth: number;
  numRows: number;
  hasShifts: boolean;
}

interface UseVirtualizationOptions {
  daysInSelectedMonth: Date[];
  dayRowCalculations: DayRowCalculation[];
  listRef: RefObject<VariableSizeList>;
  /** Optional callback to check if a day is in loading state (for estimated heights) */
  isDayLoading?: (dayIndex: number) => boolean;
  /** Estimated height for days that haven't loaded yet (default: ROW_HEIGHT_PX + GAP_HEIGHT_PX) */
  estimatedDayHeight?: number;
}

export const useVirtualization = ({
  daysInSelectedMonth,
  dayRowCalculations,
  listRef,
  isDayLoading,
  estimatedDayHeight
}: UseVirtualizationOptions) => {

  // Default estimated height for loading days (1 row + gap)
  const defaultEstimatedHeight = estimatedDayHeight ?? (ROW_HEIGHT_PX + GAP_HEIGHT_PX);

  /**
   * Calculate the height for a specific day item
   * Each day's height = (number of rows * row height) + gap + optional month divider
   * For loading days in infinite scroll mode, use estimated height
   */
  const getItemSize = useCallback((index: number): number => {
    const dayCalc = dayRowCalculations[index];

    // For days without calculation data (loading state), use estimated height
    if (!dayCalc) {
      return defaultEstimatedHeight;
    }

    // If this day is marked as loading and has no shifts data,
    // use estimated height for smoother scrolling
    if (isDayLoading?.(index) && dayCalc.numRows <= 1) {
      return defaultEstimatedHeight;
    }

    // Calculate total height: rows + gap after day
    const isLastDay = index === dayRowCalculations.length - 1;
    // Add extra bottom padding to last day to ensure it can scroll fully into view
    // This allows the last day to be scrolled past the bottom of the viewport
    let height = dayCalc.numRows * ROW_HEIGHT_PX + (isLastDay ? ROW_HEIGHT_PX : GAP_HEIGHT_PX);

    // Add space for month divider on first day of new month
    const prevDayCalc = index > 0 ? dayRowCalculations[index - 1] : null;
    const hasMonthDivider = prevDayCalc && !isSameMonth(dayCalc.dayDate, prevDayCalc.dayDate);
    if (hasMonthDivider) {
      height += MONTH_DIVIDER_HEIGHT_PX;
    }

    return height;
  }, [dayRowCalculations, isDayLoading, defaultEstimatedHeight]);

  /**
   * Find the index of a specific date in the days array
   */
  const getDayIndex = useCallback((targetDate: Date): number => {
    return daysInSelectedMonth.findIndex(day => isSameDay(day, targetDate));
  }, [daysInSelectedMonth]);

  /**
   * Get the scroll offset for a specific day index
   * NOTE: Must be defined before scroll functions that use it
   */
  const getScrollOffsetForDay = useCallback((dayIndex: number): number => {
    let offset = 0;
    for (let i = 0; i < dayIndex && i < dayRowCalculations.length; i++) {
      offset += getItemSize(i);
    }
    return offset;
  }, [dayRowCalculations, getItemSize]);

  /**
   * Scroll to today's date in the virtual list with smooth animation
   */
  const scrollToToday = useCallback(() => {
    const today = new Date();
    const todayIndex = getDayIndex(today);

    if (todayIndex >= 0 && listRef.current) {
      // Calculate the target scroll offset for center alignment
      let targetOffset = getScrollOffsetForDay(todayIndex);

      // Access the outer element for viewport height calculation and smooth scroll
      const outerElement = (listRef.current as any)._outerRef as HTMLElement | null;
      if (outerElement) {
        const viewportHeight = outerElement.clientHeight;
        const itemHeight = getItemSize(todayIndex);
        // Center alignment
        targetOffset = Math.max(0, targetOffset - (viewportHeight - itemHeight) / 2);

        outerElement.scrollTo({
          top: targetOffset,
          behavior: 'smooth'
        });
      } else {
        // Fallback to instant scroll if outer element not accessible
        listRef.current.scrollToItem(todayIndex, 'center');
      }
    }
  }, [getDayIndex, listRef, getScrollOffsetForDay, getItemSize]);

  /**
   * Scroll to a specific date in the virtual list with smooth animation
   */
  const scrollToDate = useCallback((date: Date, align: 'start' | 'center' | 'end' = 'center') => {
    const dateIndex = getDayIndex(date);

    if (dateIndex >= 0 && listRef.current) {
      // Calculate the target scroll offset
      let targetOffset = getScrollOffsetForDay(dateIndex);

      // Adjust offset based on alignment
      // Access the outer element for viewport height calculation
      const outerElement = (listRef.current as any)._outerRef as HTMLElement | null;
      if (outerElement && align !== 'start') {
        const viewportHeight = outerElement.clientHeight;
        const itemHeight = getItemSize(dateIndex);

        if (align === 'center') {
          targetOffset = Math.max(0, targetOffset - (viewportHeight - itemHeight) / 2);
        } else if (align === 'end') {
          targetOffset = Math.max(0, targetOffset - viewportHeight + itemHeight);
        }
      }

      // Use smooth scrolling via the outer element
      if (outerElement) {
        outerElement.scrollTo({
          top: targetOffset,
          behavior: 'smooth'
        });
      } else {
        // Fallback to instant scroll if outer element not accessible
        listRef.current.scrollToItem(dateIndex, align);
      }
    }
  }, [getDayIndex, listRef, getScrollOffsetForDay, getItemSize]);

  /**
   * Calculate total scrollable height for all days
   */
  const totalHeight = useMemo(() => {
    return dayRowCalculations.reduce((sum, _, index) => {
      return sum + getItemSize(index);
    }, 0);
  }, [dayRowCalculations, getItemSize]);

  /**
   * Calculate which day index is currently at the top of the viewport
   */
  const getCurrentDayIndex = useCallback((): number => {
    if (!listRef.current) return 0;

    // Access internal state of VariableSizeList
    // Note: This is a workaround as react-window doesn't expose current scroll position
    const scrollOffset = (listRef.current as any)._scrollOffset || 0;

    let cumulativeHeight = 0;
    for (let i = 0; i < dayRowCalculations.length; i++) {
      const itemHeight = getItemSize(i);
      if (cumulativeHeight + itemHeight > scrollOffset) {
        return i;
      }
      cumulativeHeight += itemHeight;
    }

    return dayRowCalculations.length - 1;
  }, [dayRowCalculations, getItemSize, listRef]);

  /**
   * Reset item sizes starting from a specific index
   * Call this when chunk data loads to recalculate heights
   */
  const resetAfterIndex = useCallback((index: number, shouldForceUpdate = true) => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(index, shouldForceUpdate);
    }
  }, [listRef]);

  /**
   * Reset all item sizes
   * Call this when significant layout changes occur
   */
  const resetAllSizes = useCallback(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
    }
  }, [listRef]);

  return {
    // Size calculations
    getItemSize,
    totalHeight,

    // Day index utilities
    getDayIndex,
    getCurrentDayIndex,
    getScrollOffsetForDay,

    // Scroll functions
    scrollToToday,
    scrollToDate,

    // Reset functions for infinite scroll
    resetAfterIndex,
    resetAllSizes,
  };
};

/**
 * Hook for calculating overscan count based on viewport height
 * Returns optimal number of days to render outside viewport
 *
 * Dynamic calculation ensures smooth scrolling at any speed by rendering
 * enough buffer items ahead of the scroll position.
 */
export const useOverscanCount = (containerHeight?: number) => {
  return useMemo(() => {
    // Calculate dynamic overscan based on viewport
    // Average day height is approximately ROW_HEIGHT_PX + GAP_HEIGHT_PX
    const avgDayHeight = ROW_HEIGHT_PX + GAP_HEIGHT_PX;
    const viewportHeight = containerHeight || 600;
    const visibleDays = Math.ceil(viewportHeight / avgDayHeight);

    // Render 75% buffer above/below viewport for smooth fast scrolling
    // Minimum of 10 days to handle variable height days
    return Math.max(10, Math.ceil(visibleDays * 0.75));
  }, [containerHeight]);
};
